import { useCallback, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as chartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
  Tooltip,
  Point,
  TooltipModel,
} from "chart.js";
import { AscCustomTooltip, TooltipData } from "./AscCustomTooltip/AscCustomTooltip";
import styles from "./AscChart.module.scss";
import classNames from "classnames";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";

chartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
  Tooltip
);

export interface AscChartProps {
  dataArr: number[] | string[];
  labels: string[];
  title?: string;
  width?: number;
  className?: string;
}

const AscChart = ({ dataArr, labels, title, width, className }: AscChartProps) => {
  const [tooltipData, setTooltipData] = useState<TooltipData>();

  const chartRef = useRef<ChartJSOrUndefined<"line", (number | Point | null)[], unknown> | null>(null);

  const customTooltip = useCallback(
    (tooltipModel: { tooltip: TooltipModel<"line"> }) => {
      if (tooltipModel.tooltip.opacity === 0) {
        return;
      }
      const chart = chartRef.current;
      const canvas = chart?.canvas;
      const canvasWidth = (chart?.canvas?.getBoundingClientRect().width || 0) - 32;

      if (canvas) {
        const columnWidth = (canvasWidth) / (dataArr?.length - 1);
        const columnLeft = tooltipModel.tooltip.x + 11;
        const left = tooltipModel.tooltip.x + 120 >= (canvasWidth - columnWidth) ? tooltipModel.tooltip.x - 100 : tooltipModel.tooltip.x;
        const top = tooltipModel.tooltip.y;
        setTooltipData({
          top,
          left,
          columnWidth,
          columnLeft,
          averageCompletion: tooltipModel?.tooltip?.dataPoints[0]?.formattedValue,
          averageGrade: tooltipModel?.tooltip?.dataPoints[0]?.formattedValue,
        });
      }
    },
    []
  );

  const data: React.ComponentProps<typeof Line>["data"] = {
    labels,
    datasets: [
      {
        label: "Sessions",
        data: dataArr,
        borderColor: "#4239FB",
        borderWidth: 2,
        tension: 0.5,
        pointBackgroundColor: "transparent",
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);
          gradient.addColorStop(0, "rgba(177,174,255, 0.4)");
          gradient.addColorStop(1, "rgba(255, 255, 255, 0.3)");
          return gradient;
        },
        pointBorderColor: "transparent",
        fill: "start",
        pointHoverRadius: 10,
        pointHoverBackgroundColor: "#4239FB",
        yAxisID: "y",
      },
    ],
  };

  const options: React.ComponentProps<typeof Line>["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
    plugins: {
      tooltip: {
        enabled: false,
        yAlign: "bottom",
        xAlign: "left",
        external: customTooltip,
      },
      legend: {
        display: true,
      },
      title: {
        display: true,
        text: title,
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
        },
        border: {
          dash: [2, 5],
        },

        ticks: {
          font: {
            size: 12,
          },
          color: "#9499B5",
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "#F1F1F1",
        },
        border: {
          dash: [2, 5],
        },
        ticks: {
          stepSize: 25,
          font: {
            size: 12,
          },
          color: "#9499B5",
        },
      },
      y1: {
        beginAtZero: true,
        type: "linear" as const,
        display: false,
      },
    },
  };

  return (
    <div className={classNames(styles.chart, className)}>
      <Line height={211} data={data} options={options} ref={chartRef} />
      {tooltipData ? <AscCustomTooltip data={tooltipData} /> : <div />}
    </div>
  );
};

export default AscChart;
